/**
 * Intake question templates — pre-built question sets per common practitioner
 * category. Tapping a template appends its questions to the current service's
 * intakeQuestions array so practitioners can launch intake in one click
 * instead of writing every question from scratch.
 *
 * Each question follows the same shape as the /services API:
 *   { id, label, type, required, options?, help? }
 *
 * ids are stable + descriptive so if a practitioner picks the same template
 * for two different services, we don't collide on random suffixes.
 */
export const INTAKE_TEMPLATES = [
  {
    id: "tarot",
    label: "Tarot / Card Reading",
    hint: "Standard 3-question set for card work",
    questions: [
      { id: "tarot_focus",  label: "What area of your life is this reading for?", type: "select", required: true,
        options: ["Love & relationships", "Career & finances", "Health", "Spiritual guidance", "Other"] },
      { id: "tarot_question", label: "What specific question would you like the cards to address?", type: "textarea", required: true,
        help: "Open-ended questions ('what should I know about…') give the richest readings." },
      { id: "tarot_context", label: "Anything else I should know before I shuffle?", type: "textarea", required: false },
    ],
  },
  {
    id: "astrology",
    label: "Astrology / Chart Reading",
    hint: "Birth data + focus area for chart work",
    questions: [
      { id: "astro_dob",   label: "Date of birth", type: "date", required: true },
      { id: "astro_time",  label: "Time of birth", type: "text", required: true, help: "Even approximate (e.g. 'early morning') helps. 'Unknown' is fine." },
      { id: "astro_place", label: "Place of birth (city, country)", type: "text", required: true },
      { id: "astro_focus", label: "What's the primary focus of this reading?", type: "select", required: true,
        options: ["Natal chart overview", "Career/purpose", "Love/relationships", "Timing (transits)", "Compatibility (with someone else)"] },
      { id: "astro_partner", label: "If compatibility: partner's DOB + place", type: "text", required: false },
    ],
  },
  {
    id: "rootwork",
    label: "Rootwork / Hoodoo / Conjure",
    hint: "Intake for candle work, mojo bags, spiritual cleansings",
    questions: [
      { id: "rw_goal", label: "What are you working toward?", type: "select", required: true,
        options: ["Love drawing", "Money / prosperity", "Protection", "Uncrossing / cleansing", "Justice / court case", "Reversal", "Other"] },
      { id: "rw_target", label: "Full name(s) of anyone the work involves", type: "text", required: false, help: "Include birthdate if known." },
      { id: "rw_situation", label: "Describe your situation", type: "textarea", required: true },
      { id: "rw_prior_work", label: "Have you or anyone else done spiritual work on this recently?", type: "select", required: false,
        options: ["No", "Yes — worked with a practitioner", "Yes — self-work", "Unsure"] },
    ],
  },
  {
    id: "energy-healing",
    label: "Reiki / Energy Healing",
    hint: "Intake for distance or in-session energy work",
    questions: [
      { id: "eh_focus", label: "What areas of your body/energy field would you like me to focus on?", type: "textarea", required: true },
      { id: "eh_current", label: "Current physical or emotional symptoms", type: "textarea", required: false },
      { id: "eh_medical", label: "Any medical conditions I should be aware of?", type: "textarea", required: false,
        help: "Energy work complements — never replaces — medical care." },
      { id: "eh_experience", label: "Have you received energy work before?", type: "select", required: false,
        options: ["Never", "Once or twice", "Regularly", "I'm a practitioner myself"] },
    ],
  },
  {
    id: "mediumship",
    label: "Mediumship / Ancestral Reading",
    hint: "Intake for spirit / ancestor communication",
    questions: [
      { id: "med_seeking", label: "Who or what are you hoping to hear from?", type: "textarea", required: true,
        help: "Ancestors will come as they come — but naming your intention helps the connection." },
      { id: "med_first_name", label: "First name(s) of any specific person you'd like to reach", type: "text", required: false },
      { id: "med_relationship", label: "Relationship to you", type: "text", required: false },
      { id: "med_open", label: "Are you open to messages you didn't ask for?", type: "select", required: true,
        options: ["Yes — whatever comes through", "Only what I asked for"] },
    ],
  },
  {
    id: "ifa-divination",
    label: "Ifá / Orisha Consultation",
    hint: "Intake for babaláwo / iyánífá consultations",
    questions: [
      { id: "ifa_context", label: "What brings you to consult Ifá today?", type: "textarea", required: true },
      { id: "ifa_urgency", label: "Is there an urgent decision or event this relates to?", type: "textarea", required: false },
      { id: "ifa_prior", label: "Have you had a divination reading before?", type: "select", required: false,
        options: ["No — first time", "Yes — with another elder", "Yes — with you before"] },
      { id: "ifa_language", label: "Preferred language for the reading", type: "select", required: false,
        options: ["English", "Yorùbá", "Portuguese", "Spanish", "Other"] },
    ],
  },
  {
    id: "candle-lighting",
    label: "Candle Lighting / Petition",
    hint: "Intake for altar candle work + petitions",
    questions: [
      { id: "cl_intention", label: "Your petition / intention", type: "textarea", required: true,
        help: "Speak it clearly, present tense — the clearer the ask, the cleaner the work." },
      { id: "cl_names", label: "Names to include on the petition", type: "text", required: false },
      { id: "cl_deadline", label: "Is there a date this needs to manifest by?", type: "date", required: false },
      { id: "cl_photo", label: "Would you like me to send you a photo of the lit candle?", type: "select", required: false,
        options: ["Yes", "No — keep it private"] },
    ],
  },
];

/** Small helper — dedupe questions when appending a template so a
 *  practitioner tapping the same template twice doesn't get duplicates. */
export function mergeTemplateQuestions(existing = [], templateQuestions = []) {
  const ids = new Set((existing || []).map((q) => q?.id).filter(Boolean));
  const additions = templateQuestions.filter((q) => !ids.has(q.id));
  return [...(existing || []), ...additions];
}
